import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/lib/supabase';

interface SubscriptionPlanFamily {
  id: string;
  name: string;
  description: string | null;
  is_active?: boolean;
  target?: string;
}

interface EditFamilyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  family: SubscriptionPlanFamily | null;
  onSuccess: (updatedFamily: SubscriptionPlanFamily) => void;
}

export default function EditFamilyDialog({ 
  open, 
  onOpenChange, 
  family, 
  onSuccess 
}: EditFamilyDialogProps) {
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTarget, setEditTarget] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Reset form when family changes
  useEffect(() => {
    if (family) {
      setEditName(family.name);
      setEditDescription(family.description || '');
      setEditTarget(family.target || '');
    }
  }, [family]);

  const handleClose = () => {
    setEditName('');
    setEditDescription('');
    setEditTarget('');
    setLoading(false);
    onOpenChange(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!family) return;

    setLoading(true);

    try {
      const { error } = await supabase
        .from('subscription_plan_families')
        .update({
          name: editName,
          description: editDescription,
          target: editTarget,
          updated_at: new Date().toISOString()
        })
        .eq('id', family.id);

      if (error) throw error;

      const updatedFamily = {
        ...family,
        name: editName,
        description: editDescription,
        target: editTarget
      };

      toast({
        title: "Família atualizada",
        description: "A família de planos foi atualizada com sucesso.",
      });

      onSuccess(updatedFamily);
      handleClose();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao atualizar família",
        description: "Não foi possível atualizar a família. Por favor, tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar Família de Planos</DialogTitle>
          <DialogDescription>
            Faça as alterações necessárias na família de planos.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">Nome</Label>
            <Input
              id="edit-name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Nome da família"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-description">Descrição</Label>
            <Textarea
              id="edit-description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Descrição da família"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-target">Target</Label>
            <Input
              id="edit-target"
              value={editTarget}
              onChange={(e) => setEditTarget(e.target.value)}
              placeholder="Ex: Empresas de médio porte"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
